/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Modal, Spin, Input, message, Form, Row, Col,
} from 'antd';
import Table from 'components/Table';
import { CheckCircleOutlined, UserOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import { ESTATUS_AUTORIZADO } from 'utils/constants';
import {
  autorizarAutorizacionContratacionDetalle,
  getAutorizacionesContratacionesDetalle,
  putAutorizarContratacionMasiva,
} from 'api/autorizarContratacionesDetalles'; // Ajusta la ruta a tu api real
import { getEstadosGlobales } from 'api/catalogos';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import { getUsuarios } from 'api/usuarios/usuarios';
import { getUsuariosLimitaciones } from 'api/usuarios/usuariosLimitaciones';
import moment from 'moment';

const { TextArea } = Input;

const AutorizacionDetalleModal = ({
  open,
  onClose,
  idAutorizacion,
  candidatoNombre,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [estados, setEstados] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [limitaciones, setLimitaciones] = useState([]);

  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [recordToAuthorize, setRecordToAuthorize] = useState(null);
  const [formAutorizar] = Form.useForm(); // Instancia del form

  const fetchData = async () => {
    setLoading(true);
    try {
      // eslint-disable-next-line max-len
      const _data = await getAutorizacionesContratacionesDetalle({ autorizacion_contratacion: idAutorizacion });
      setData(_data.results || []);
    } catch (err) {
      onError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      try {
        await checkToken();
        const [
          _estados,
          _usuarios,
          _limitaciones,
          _data,
        ] = await Promise.all([
          getEstadosGlobales(),
          getUsuarios(),
          getUsuariosLimitaciones(),
          getAutorizacionesContratacionesDetalle({ autorizacion_contratacion: idAutorizacion }),
        ]);

        if (mounted) {
          setEstados(_estados);
          setUsuarios(_usuarios);
          setLimitaciones(_limitaciones.results || _limitaciones);
          setData(_data.results || []);
        }
      } catch (error) {
        onError(error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (idAutorizacion && open) {
      fetchAll();
    } else {
      setData([]);
    }

    return () => { mounted = false; };
  }, [idAutorizacion, open]);

  const handlePreAutorizar = (record) => {
    setRecordToAuthorize(record);
    // Limpiamos el form y seteamos valores iniciales si existen
    formAutorizar.resetFields();
    formAutorizar.setFieldsValue({
      observaciones: record.observaciones || '',
    });
    setVisibleAutorizar(true);
  };

  const onAutorizarFinish = async () => {
    if (!recordToAuthorize) return;

    try {
      setLoading(true);

      // 1. Validar campos requeridos
      await formAutorizar.validateFields();

      // 2. Obtener valores
      const values = formAutorizar.getFieldsValue();

      // 3. Preparar el Payload (Array con ID y Observaciones)
      const payload = [
        {
          id: recordToAuthorize.id,
          observaciones: values.observaciones || 'AUTORIZADO',
        },
      ];

      // 4. Llamada API PUT
      const response = await putAutorizarContratacionMasiva(payload);

      if (response) {
        message.success('Autorización realizada correctamente.');

        // Refrescar datos
        await fetchData();

        // Cerrar modal y limpiar
        setVisibleAutorizar(false);
        formAutorizar.resetFields();
        setRecordToAuthorize(null);
      }
    } catch (error) {
      message.error('Error al autorizar.');
    } finally {
      setLoading(false);
    }
  };

  // Helper para obtener nombre del usuario
  const getNombreUsuario = (val) => {
    if (!val) return 'N/A';
    const limitacion = limitaciones.find((l) => l.id === val);
    if (limitacion) {
      const usuarioReal = usuarios.find((u) => u.id === limitacion.usuario);
      if (usuarioReal) {
        const nombre = `${usuarioReal.first_name || ''} ${usuarioReal.last_name || ''}`.trim();
        return nombre || usuarioReal.email;
      }
    }
    return `Nivel ID: ${val}`;
  };

  const columns = [
    {
      titleText: 'Detalle de Autorización',
      children: [
        {
          title: 'Usuario',
          dataIndex: 'usuario_limitacion_de_informacion',
          key: 'usuario_limitacion_de_informacion',
          width: 150,
          render: (val) => getNombreUsuario(val),
        },
        {
          title: 'Observaciones',
          dataIndex: 'observaciones',
          key: 'observaciones',
          width: 250,
          render: (val) => val || '-',
        },
        {
          title: 'Fecha',
          dataIndex: 'fecha_de_creacion',
          key: 'fecha_de_creacion',
          width: 150,
          render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
        },
        {
          title: 'Estatus',
          dataIndex: 'estados_globales',
          key: 'estados_globales',
          width: 150,
          render: (val) => {
            const estado = estados.find((e) => e.id === val);
            return estado ? estado.descripcion : 'Pendiente';
          },
        },
      ],
    },
  ];
  return (
    <>
      {/* --- MODAL PRINCIPAL (TABLA) --- */}
      <Modal
        title={`Autorizaciones para: ${candidatoNombre || '...'}`}
        open={open}
        onCancel={onClose}
        width={1000}
        footer={null}
        destroyOnClose
      >
        <Spin spinning={loading}>
          <Table
            cols={columns}
            data={data}
            customActions={[
              // {
              //   text: 'Autorizar',
              //   icon: <CheckCircleOutlined />,
              //   className: 'primary',
              //   onClick: (record) => handlePreAutorizar(record),
              //   disabled: (record) => record.estados_globales === ESTATUS_AUTORIZADO
              //      || record.estados_globales?.id === ESTATUS_AUTORIZADO,
              // },
            ]}
          />
        </Spin>
      </Modal>

      {/* --- MODAL DE AUTORIZAR (ESTILO TU EJEMPLO) --- */}
      <Modal
        width="40%"
        open={visibleAutorizar}
        closable={false}
        maskClosable={!loading}
        keyboard={!loading}
        footer={null}
        forceRender
        title={(
          <FormSubmitControls
            label="Confirmar Autorización"
            onFinish={() => formAutorizar.submit()}
            onCancel={() => {
              setVisibleAutorizar(false);
              formAutorizar.resetFields();
            }}
            loading={loading}
          />
        )}
      >
        <Form
          layout="vertical"
          form={formAutorizar}
          onFinish={onAutorizarFinish}
        >
          <Row gutter={16}>
            <Col span={24}>
              <p>
                <strong>Autorizando nivel de: </strong>
                {recordToAuthorize
                && getNombreUsuario(recordToAuthorize.usuario_limitacion_de_informacion)}
              </p>
            </Col>

            <Col span={24}>
              <Form.Item
                name="observaciones"
                label="Observaciones"
                rules={[
                  { required: true, message: 'Ingrese una observación para autorizar' },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Ej. AUTORIZADO POR OFICIAL MAYOR"
                  maxLength={250}
                  showCount
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default AutorizacionDetalleModal;
